import { useState } from "react";
import { motion } from "framer-motion";
import { TrendingUp, Users, DollarSign } from "lucide-react";
import CASE_STUDIES from "@/data/caseStudiesData";
import DetailsModal from "@/components/landing/DetailsModal";
import CaseStudyChart from "@/components/landing/CaseStudyChart";

// Inline stats kept on cards (the modal carries the full story)
const STATS = {
  "dtc-skincare-scale": [
    { icon: DollarSign, value: "7.5x", label: "Revenue lift" },
    { icon: TrendingUp, value: "5.8x", label: "Blended ROAS" },
    { icon: Users, value: "42K", label: "New customers" },
  ],
  "b2b-saas-pipeline": [
    { icon: TrendingUp, value: "+238%", label: "Pipeline growth" },
    { icon: DollarSign, value: "-37%", label: "CAC reduction" },
    { icon: Users, value: "1.4K", label: "Qualified demos" },
  ],
};

export default function CaseStudies() {
  const [active, setActive] = useState(null);

  return (
    <section
      id="case-studies"
      data-testid="case-studies-section"
      className="relative py-14 md:py-32 overflow-hidden"
    >
      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-10 md:mb-16 gap-6">
          <div className="max-w-2xl">
            <div className="text-[10px] font-mono-acc uppercase tracking-[0.3em] text-cyan-400 mb-3">
              / Case Studies
            </div>
            <h2 className="font-clash text-3xl md:text-5xl font-semibold tracking-tight leading-[1.05]">
              Real P&L outcomes. <br />
              <span className="text-gradient-neon">Receipts, not promises.</span>
            </h2>
          </div>
          <a
            href="#contact"
            className="inline-flex items-center gap-2 text-sm text-zinc-300 hover:text-white transition-colors group self-start md:self-end"
            data-testid="case-studies-view-all"
          >
            Request a reference call
            <span className="transition-transform group-hover:translate-x-1">→</span>
          </a>
        </div>

        <div className="flex flex-col gap-10">
          {CASE_STUDIES.map((c, i) => {
            const reverse = i % 2 === 1;
            const stats = STATS[c.key] || [];
            return (
              <motion.article
                key={c.key}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.7 }}
                className="grid md:grid-cols-12 gap-6 md:gap-10 items-center rounded-3xl glass-strong p-6 md:p-10 ring-glow"
                data-testid={`case-study-${i}`}
              >
                <div className={`md:col-span-7 relative rounded-2xl overflow-hidden bg-gradient-to-br from-[#0d0d12] via-[#0a0a0d] to-[#0d0d12] border border-white/[0.06] p-4 sm:p-7 ${reverse ? "md:order-2" : ""}`}>
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-4 sm:mb-3">
                    <div className="inline-flex w-fit items-center rounded-full bg-black/40 backdrop-blur px-3 py-1 text-[10px] font-mono-acc uppercase tracking-[0.22em] text-cyan-300 border border-white/10">
                      {c.tag}
                    </div>
                    <div className="text-[10px] font-mono-acc uppercase tracking-[0.22em] text-zinc-500 sm:text-right">
                      {c.chart?.title || "Performance"}
                    </div>
                  </div>
                  <CaseStudyChart data={c.chart} />
                </div>

                <div className={`md:col-span-5 ${reverse ? "md:order-1" : ""}`}>
                  <h3 className="font-clash text-2xl md:text-3xl font-semibold tracking-tight leading-tight">
                    {c.name}
                  </h3>
                  <p className="mt-4 text-zinc-400 text-sm md:text-base leading-relaxed">
                    {c.tagline}
                  </p>

                  <div className="mt-7 grid grid-cols-3 gap-3">
                    {stats.map((s) => {
                      const Icon = s.icon;
                      return (
                        <div key={s.label} className="rounded-xl bg-white/[0.03] border border-white/[0.06] p-3">
                          <Icon size={14} className="text-cyan-300 mb-2" />
                          <div className="font-clash text-xl font-semibold text-white">{s.value}</div>
                          <div className="text-[10px] font-mono-acc uppercase tracking-[0.18em] text-zinc-500 mt-1">{s.label}</div>
                        </div>
                      );
                    })}
                  </div>

                  <button
                    type="button"
                    onClick={() => setActive(c)}
                    className="mt-7 inline-flex items-center gap-2 text-sm text-white hover:text-cyan-300 transition-colors group"
                    data-testid={`case-study-cta-${c.key}`}
                  >
                    Read the full breakdown
                    <span className="transition-transform group-hover:translate-x-1">→</span>
                  </button>
                </div>
              </motion.article>
            );
          })}
        </div>

        <p className="mt-10 text-center text-xs text-zinc-500 max-w-2xl mx-auto leading-relaxed">
          Reference calls with named clients available for qualified strategic
          enquiries — book a call to request introductions.
        </p>
      </div>

      {active && (
        <DetailsModal
          item={active}
          onClose={() => setActive(null)}
          testIdPrefix="case-study-details"
        />
      )}
    </section>
  );
}
